import useSWR from 'swr';
import { useCallback, useContext, useEffect, useState } from 'react';
import { AppContext } from 'providers/AppProvider';
import { AssetRetrievalMandate, GeneralMandate } from 'types';
import { FLOW_TYPE } from 'consts';
import { isDefined, isInputHasEmail, isInputHasKennitala } from 'helpers';

export function useMandate() {
	const ctx = useContext(AppContext);
	const { config, entityType, flowType, fetchRelations, http, state, t } = ctx;
	const { processInfo } = state.context;
	const { agreementProcessId = '' } = processInfo || {};
	const mandatesURL = `${config.API.mandates}/${agreementProcessId}`;
	const { data } = useSWR(mandatesURL);
	const [isMandateVisible, setIsMandateVisible] = useState(false);
	const [isAssetVisible, setIsAssetVisible] = useState(false);
	const [mandatesCounter, setMandatesCounter] = useState([1]);

	const handleIsMandateVisible = useCallback(() => {
		setIsMandateVisible(!isMandateVisible);
	}, [isMandateVisible]);

	const handleAssetRetrieval = useCallback(() => {
		setIsAssetVisible(!isAssetVisible);
	}, [isAssetVisible]);

	const submit = () => {
		let isAssetValid = isAssetVisible === false;
		let isMandateValid = isMandateVisible === false;

		const payload = {
			assetRetrievalMandates: [] as AssetRetrievalMandate[],
			generalMandates: [] as GeneralMandate[],
			ssn: state.user.kennitala
		};

		if (isAssetVisible) {
			const input = document.querySelector<HTMLInputElement>('input[name="assetRetrievalMandates"]');
			if (input && isDefined(input.value)) {
				isAssetValid = true;
				payload.assetRetrievalMandates.push({ agreementProcessId, name: input.value });
			}
		}

		if (isMandateVisible) {
			const emailInputs = Array.from(document.querySelectorAll<HTMLInputElement>('input[name="email[]"]'));
			const ssnInputs = Array.from(document.querySelectorAll<HTMLInputElement>('input[name="ssn[]"]'));
			const isValidEmails = emailInputs.every(isInputHasEmail);
			const isValidKennitalas = ssnInputs.every(isInputHasKennitala);
			if (isValidEmails && isValidKennitalas) {
				isMandateValid = true;
				ssnInputs.forEach((input, i) => {
					payload.generalMandates.push({
						agreementProcessId,
						email: emailInputs[i].value,
						kennitala: input.value
					});
				});
			}
		}

		if (isAssetValid && isMandateValid) {
			const msg = t('submit_label', 'mandate');
			http.post(mandatesURL, payload, msg).then(() => {
				fetchRelations(true);
			});
		}
	};

	const handleAddMandate = useCallback(() => {
		if (mandatesCounter.length < 3) {
			mandatesCounter.push(mandatesCounter.length + 1);
			setMandatesCounter([...mandatesCounter]);
		}
	}, [mandatesCounter]);

	const handleRemoveMandate = useCallback(() => {
		if (mandatesCounter.length > 1) {
			mandatesCounter.pop();
			setMandatesCounter([...mandatesCounter]);
		}
	}, [mandatesCounter]);

	const isAssetRetrievalSectionVisible = flowType === FLOW_TYPE.EB || flowType === FLOW_TYPE.ER;
	const prefix = `${flowType}_${entityType}_`;
	const assetDescription = t(`${prefix}asset_retrieval_mandates_check_description`, 'mandate');
	const assetNameSpace = 'asset_retrieval_mandates_check_';
	const assetRetrievalCheckboxLabel = t(`${prefix}${assetNameSpace}checkbox_label`, 'mandate');
	const description = t(`${prefix}description`, 'mandate');
	const mandateCheckboxLabel = t(`${prefix}show_form_checkbox_label`, 'mandate');
	const terms = t(`${prefix}terms`, 'mandate');
	const title = t('title', 'mandate');

	useEffect(() => {
		if (data) {
			data.assetRetrievalMandates.length && setIsAssetVisible(true);
			data.generalMandates.length && setIsMandateVisible(true);
		}
	}, [data]); // eslint-disable-line

	return {
		...ctx,
		assetDescription,
		assetRetrievalCheckboxLabel,
		data,
		description,
		handleAddMandate,
		handleAssetRetrieval,
		handleIsMandateVisible,
		handleRemoveMandate,
		isAssetRetrievalSectionVisible,
		isAssetVisible,
		isMandateVisible,
		mandateCheckboxLabel,
		mandatesCounter,
		submit,
		terms,
		title
	};
}
